<template>
  <div
    :id="activityComponentId"
    class="module-plan-activity-component"
    :class="activityComponentClass"
  >
    <AccordionWrapper
      class="module-plan-accordion-component accordion-activity"
      @state-change="toggleActivityAccordion"
    >
      <cv-accordion-item :open="isAccordionOpen">
        <template #title>
          <match-indicator v-if="checkActivityIdMatched(activity.id)" />
          <div class="wrapper">
            <div class="component-content">
              <!-- Icon & title -->
              <dl class="field-set title accordion-title">
                <dd class="field-value">
                  <div v-if="showSelectButtonForActivity" class="activity-checkbox" @click.stop>
                    <input-checkbox
                      :id="`activity-list-${activity.id.toString()}`"
                      :value="activity.id.toString()"
                      :selected-values="selectedActivities"
                      :disabled="isCheckBoxDisabledForActivity"
                      @change="handleCheckboxChange(activity.id, $event)"
                    />
                  </div>
                  <div v-if="showRadioButtonForActivity" class="activity-checkbox" @click.stop>
                    <input-radio
                      :id="`activity-list-${activity.id.toString()}`"
                      kind="grey"
                      :value="activity.id.toString()"
                      :selected-value="selectedActivity"
                      :disabled="isRadioButtonDisabledForDifferentActivityType(activity)"
                      @change="handleRadioChange"
                    />
                  </div>
                  <span
                    class="icon"
                    :class="{
                      'hide-section-activity-number': hideSectionActivityNumber,
                    }"
                  >
                    <svg-icon :key="activityIconType" :name="activityIconType" kind="indicative" />
                  </span>
                  <span class="accordion-text">{{ activity.title }}</span>

                  <svg-icon
                    v-if="hasPendingReleaseQuiz"
                    class="pending-release"
                    name="Pen16"
                    kind="indicative"
                  />

                  <!--                  TODO-->
                  <!-- Meeting Link Icon -->
                  <!--                    <span class="icon-video" v-if="showMeetingLinkIcon">-->
                  <!--                      <svg-icon name="VideoConference24" kind="action" />-->
                  <!--                    </span>-->

                  <!--                    <div v-if="showActivityPrerequisite" class="activity-prerequisite">-->
                  <!--                      <svg-icon name="Lock24" kind="indicative" />-->
                  <!--                      <label-component-->
                  <!--                        class="resource-prerequisite"-->
                  <!--                        :text="prerequisiteLabel"-->
                  <!--                        kind="complete"-->
                  <!--                      />-->
                  <!--                    </div>-->
                </dd>
              </dl>

              <!-- Interactive team activity & quiz icon -->
              <div v-if="showActivityMetaInfo" class="meta-info" @click.stop>
                <cv-interactive-tooltip
                  v-if="isGroupWork"
                  class="field-tooltip differentiated-access"
                  @click.stop.prevent
                >
                  <template #trigger>
                    <svg-icon name="StudentGroups24" kind="indicative" />
                  </template>
                  <template #content>
                    <ol v-if="!isDifferentiatedAccess">
                      <li>{{ t("lesson_settings_groupwork_tooltip_all_student") }}</li>
                    </ol>
                    <ol v-else-if="!hasGroupSelected">
                      <li>{{ t("lesson_settings_groupwork_tooltip_no_student") }}</li>
                    </ol>
                    <template v-else>
                      <ol class="differentiated-access-group-list-tooltip">
                        <li
                          v-for="(group, $index) in activityDifferentiateAccess?.groups"
                          :key="$index"
                        >
                          {{ $index + 1 }}. {{ group }}
                        </li>
                      </ol>
                      <span v-if="hasMoreExcluded">
                        {{
                          truncateDifferentiateAccessMessage(
                            activityDifferentiateAccess?.moreExcluded,
                          )
                        }}
                      </span>
                    </template>
                  </template>
                </cv-interactive-tooltip>
                <label-component
                  v-if="showQuizStatus"
                  class="quiz"
                  :kind="quizStatusKind"
                  :text="quizStatusLabel"
                />
              </div>
            </div>
          </div>
        </template>

        <template #content>
          <div v-if="showTime" class="recommended-time">
            <svg-icon name="Time16" kind="indicative" />
            <time>{{ showTime }}</time>
          </div>
          <template v-if="!isBlank">
            <div class="wrapper" @click.stop>
              <div v-if="isTeacher" class="notes">
                <h6>{{ t("module_plan_notes") }}</h6>
                <rich-text
                  :lazyload="false"
                  :content="activityNotes"
                  :expandable="true"
                  :expanded="true"
                />
              </div>
              <div v-if="isActivityQuiz" class="description">
                <h6>{{ t("module_plan_description") }}</h6>
                <rich-text
                  :lazyload="false"
                  :content="activityDescription"
                  :expandable="true"
                  :expanded="true"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <empty-placeholder>
              <template #icon>
                <AnnotationCardsEmpty />
              </template>
              {{
                isActivityQuiz
                  ? t("module_plan_no_notes_and_description")
                  : t("module_plan_no_notes")
              }}
            </empty-placeholder>
          </template>
        </template>
      </cv-accordion-item>
    </AccordionWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import AnnotationCardsEmpty from "@images/placeholder/empty-states/AnnotationCardsEmpty.svg?component";
import { FAILED_REASON } from "@plugins/rule-engine/rule.constant.ts";
import quizActivityApi from "@apis/quiz.activity.api.ts";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant.ts";
import CoursePlanPrerequisiteMixin from "@plugins/course-lesson-plan/mixin/course.plan.prerequisite.mixin.ts";
import {
  ACTIVITY_RESTRICTION,
  MARKING_STATUS,
  QUIZ_TEST_MODE,
} from "@plugins/course-lesson-plan/course.constant.ts";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import DateTimeUtils from "@utils/datetime.utils.ts";
import { COMPONENT_AUTHORING_COPILOT_TYPE } from "@modules/editor/service/editor.constant";
import { useStore } from "vuex";
import AccordionWrapper from "@plugins/common/components/AccordionWrapper.vue";
import MatchIndicator from "@plugins/common/components/MatchIndicator.vue";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import InputRadio from "@plugins/common/components/InputRadio.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import LabelComponent from "@plugins/common/components/LabelComponent.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import { useActivityIcon } from "@plugins/common/composables/activity-icon.ts";
import { NAMESPACE } from "@modules/search/service/search.constant.ts";
import AuthoringCopilotTemplateSelectionMixin from "@modules/editor/mixin/authoring.copilot.template.selection.mixin.ts";
import { useVm } from "@utils/vm.ts";
import {
  LessonSelectionModeEnum,
  LessonSelectionTypeEnum,
  SearchModuleDetail,
} from "@modules/resource-search/resource-search-types.ts";

defineOptions({
  name: "ModulePlanActivityComponent",
  mixins: [CoursePlanPrerequisiteMixin, AuthoringCopilotTemplateSelectionMixin],
});

interface Props {
  section?: Record<string, unknown>;
  activity: Record<string, unknown>;
  isAccordionOpen: boolean;
  hideSectionActivityNumber: boolean;
  params: SearchModuleDetail | null;
  selectedActivities?: string[];
  selectionMode?: string;
}

const props = withDefaults(defineProps<Props>(), {
  section: () => ({}),
  selectedActivities: () => [],
  selectionMode: "",
});

const {
  getActivityIconName,
  getActivityNoAccessIconName,
  getActivityCompletedIconName,
  getActivityCompletedDisabledIconName,
  getActivityRestrictedIconName,
} = useActivityIcon();
const { vm } = useVm();

const emit = defineEmits<{
  toggleActivityAccordion: [
    value: { data: { section: Record<string, unknown>; activityId: string; expanded: boolean } },
  ];
  updateSelectedActivity: [activityId: string, isChecked?: boolean] | [activityId: string];
}>();

const { t } = useI18n();
const vuexStore = useStore();

const description = ref("");

const isTeacher = computed(() => vuexStore.getters[`${NAMESPACE.AUTH}/isTeacher`]);

const activityComponentId = computed(() => `activity-${props.activity?.id}`);

const activityComponentClass = computed(() => ({
  disabled: !isPageAccessible.value || !hasParentAccess.value,
}));

const sectionSettings = computed(() => props.section.settings);
const activityAccess = computed(() => props.activity.access);
const activityStatusInfo = computed(() => props.activity.statusInfo);
const activityProgress = computed(() => props.activity.progress);
const activityDifferentiateAccess = computed(() => props.activity.differentiateAccess);
const activitySetting = computed(() => props.activity.settings);
const activityQuizTestMode = computed(() => props.activity.quizTestMode);

const activityNotes = computed(() => props.activity.notes || t("module_plan_no_notes"));
const activityDescription = computed(() => description.value || t("module_plan_no_description"));

const activityQuizStatus = computed(() => activityStatusInfo.value?.markStatus);

const activityIconType = computed(() => {
  if (!hasParentAccess.value || !hasPageToggleAccess.value) {
    return getActivityNoAccessIconName(props.activity);
  }
  if (activityStatusInfo.value?.completed) {
    return isPageAccessible.value
      ? getActivityCompletedIconName(props.activity)
      : getActivityCompletedDisabledIconName(props.activity);
  }
  if (!hasPageDifferentiatedAccess.value) {
    return getActivityRestrictedIconName(props.activity);
  }
  return getActivityIconName(props.activity);
});

const quizStatusKind = computed(() => {
  const quizType = activityQuizTestMode.value;
  const status = activityQuizStatus.value;

  if (status === MARKING_STATUS.RELEASE) {
    return quizType === QUIZ_TEST_MODE.TEST_TEACHER_RELEASE ? "marked" : "completed";
  }

  if ([MARKING_STATUS.MARKED, MARKING_STATUS.PENDING_MARK].includes(status)) {
    return "pending-mark";
  }

  if (isOverdue.value) {
    return "overdue";
  }

  return "incomplete";
});

const quizStatusLabel = computed(() => {
  const quizType = activityQuizTestMode.value;
  const status = activityQuizStatus.value;

  if (status === MARKING_STATUS.RELEASE) {
    return quizType === QUIZ_TEST_MODE.TEST_TEACHER_RELEASE
      ? t("quiz_component_label_status_released")
      : t("quiz_component_label_status_completed");
  }

  if ([MARKING_STATUS.MARKED, MARKING_STATUS.PENDING_MARK].includes(status)) {
    return t("quiz_component_label_status_to_mark");
  }

  if (isOverdue.value) {
    return t("quiz_component_label_status_overdue");
  }

  return t("quiz_component_label_status_incompleted");
});

const showTime = computed(() => {
  const time = props.activity.recommendedTime;
  return time ? formatTime(time) : null;
});

const showActivityMetaInfo = computed(() => showQuizStatus.value || isGroupWork.value);

const showQuizStatus = computed(() => !!activityQuizTestMode.value && !!activityStatusInfo.value);

const isOverdue = computed(() => {
  const endDateTime = sectionSettings.value?.endDateTime;
  if (!endDateTime) return false;

  const dateTime = DateTimeFormatter.formatLocalDateTimeToDateTime(endDateTime);
  return DateTimeUtils.isNowAfterDate(dateTime);
});

const isBlank = computed(() => {
  const noDescription = activityDescription.value === t("module_plan_no_description");

  const noNotes = activityNotes.value === t("module_plan_no_notes");

  return noDescription && (!isTeacher.value || noNotes);
});

const isGroupWork = computed(() => activitySetting.value?.groupWork === true);

const isActivityQuiz = computed(() => props.activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ);

const isDifferentiatedAccess = computed(
  () => activitySetting.value?.restriction !== ACTIVITY_RESTRICTION.NONE,
);

const isPageAccessible = computed(
  () => hasPageToggleAccess.value && hasPageDifferentiatedAccess.value,
);

const hasPendingReleaseQuiz = computed(() => activityProgress.value?.numPendingRelease > 0);

const hasGroupSelected = computed(() => activitySetting.value?.restrictionSettings.length > 0);

const hasMoreExcluded = computed(() => activityDifferentiateAccess.value?.moreExcluded > 0);

const hasPageToggleAccess = computed(
  () => !activityAccess.value?.failedReasons.includes(FAILED_REASON.ACCESSIBLE_ACCESS_DENIED),
);

const hasParentAccess = computed(
  () => !activityAccess.value?.failedReasons.includes(FAILED_REASON.PARENT_ACCESS_DENIED),
);

const hasPageDifferentiatedAccess = computed(
  () =>
    !activityAccess.value?.failedReasons.includes(FAILED_REASON.GROUPWORK_ACCESSIBLE_ACCESS_DENIED),
);

const isFromAuthoringCopilotViewDetails = computed(() => vm.isFromAuthoringCopilotViewDetails);

const isFromLessonActivityAuthoringCopilot = computed(
  () => props?.params?.isFromLessonActivityAuthoringCopilot,
);

const isCheckBoxDisabledForActivity = computed(() => {
  if (isFromAuthoringCopilotViewDetails.value || isFromLessonActivityAuthoringCopilot.value)
    return true;
  return props.selectionMode === LESSON_ACTIVITY_TYPE.SECTION;
});

const showSelectButtonForActivity = computed(() => {
  return (
    props.params?.selectionMode === LessonSelectionModeEnum.multiple &&
    props.params.selectionType === LessonSelectionTypeEnum.activityOrSection
  );
});

const showRadioButtonForActivity = computed(() => {
  return (
    props.params?.selectionMode === LessonSelectionModeEnum.single &&
    props.params.selectionType === LessonSelectionTypeEnum.activityOrSection
  );
});

const selectedActivity = computed(() => {
  return props.selectedActivities.includes(String(props.activity.id))
    ? String(props.activity.id)
    : null;
});

onMounted(() => {
  if (props.isAccordionOpen) {
    fetchQuizActivityDescription();
  }
});

const toggleActivityAccordion = (event: { state: boolean[] }) => {
  emit("toggleActivityAccordion", {
    data: {
      section: props.section,
      activityId: props.activity.id,
      expanded: event.state[0],
    },
  });
};

const truncateDifferentiateAccessMessage = (moreExcluded: number) => {
  return t("lesson_settings_groupwork_tooltip_truncate", {
    moreExcluded: moreExcluded,
  });
};

const fetchQuizActivityDescription = () => {
  if (isActivityQuiz.value && isTeacher) {
    quizActivityApi.fetchQuizActivityInfo(props.activity.id).then((response) => {
      description.value = response.data.description;
    });
  }
};

const checkActivityIdMatched = (activityId: string) => {
  const activity = props?.params?.highlights?.activities?.find(
    (activity: { id: string }) => activity.id === activityId,
  );
  return !!activity; // Returns true if a activity is found, false otherwise
};

const isRadioButtonDisabledForDifferentActivityType = (activity: Record<string, unknown>) => {
  if (isFromAuthoringCopilotViewDetails.value) return true;
  switch (props?.params?.activityType) {
    case COMPONENT_AUTHORING_COPILOT_TYPE.QUIZ:
      return activity.type !== LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ;
    case COMPONENT_AUTHORING_COPILOT_TYPE.ACTIVITY:
      return activity.type !== LESSON_ACTIVITY_TYPE.ACTIVITY;
  }
  return true;
};

const handleRadioChange = (activityId: string) => {
  emit("updateSelectedActivity", activityId);
};

const handleCheckboxChange = (activityId: string, checked: string[]) => {
  const isChecked = checked.includes(activityId.toString());
  emit("updateSelectedActivity", activityId, isChecked);
};

const formatTime = (seconds: number) => {
  const hours = DateTimeUtils.getHours(seconds);
  const mins = DateTimeUtils.getMinutes(seconds);
  const secs = DateTimeUtils.getSeconds(seconds);

  let timeString = "";

  if (hours > 0) {
    timeString += `${hours} hr `;
  }
  if (mins > 0) {
    timeString += `${mins} min `;
  }
  if (secs > 0 || timeString === "") {
    // Show seconds if there are no hours or minutes
    timeString += `${secs} sec`;
  }

  return timeString.trim();
};
</script>

<style></style>
